import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Newspaper, Calendar, BookOpen, Globe, CalendarCheck, ClipboardCheck,
  ArrowRight, MessageSquare, Mail, AlertTriangle, Plus, Eye, TrendingUp,
  Clock, CheckCircle2, XCircle,
} from 'lucide-react';
import { api, absoluteUrl } from '../../lib/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [duesDist, setDuesDist] = useState([]);
  const [topInterpreters, setTopInterpreters] = useState(null);
  const [topPeriod, setTopPeriod] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get('/admin/stats').then(({ data }) => alive && setStats(data)).catch(() => {}),
      api.get('/admin/stats/languages').then(({ data }) => alive && setLanguages(data || [])).catch(() => {}),
      api.get('/admin/stats/dues-distribution').then(({ data }) => alive && setDuesDist(data || [])).catch(() => {}),
    ]).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Top interpreters re-fetches when the period selector changes.
  useEffect(() => {
    let alive = true;
    api.get(`/admin/stats/top-interpreters?days=${topPeriod}&limit=10`)
      .then(({ data }) => { if (alive) setTopInterpreters(data); })
      .catch(() => { if (alive) setTopInterpreters({ rows: [], total_requests_in_period: 0, period_days: topPeriod }); });
    return () => { alive = false; };
  }, [topPeriod]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><div className="w-10 h-10 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin" /></div>;
  }

  const s = stats || {};
  const interp = s.interpreters || {};
  const visibilityPct = interp.total ? Math.round(100 * (interp.visible_public || 0) / interp.total) : 0;

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800">Panel de Administración</h1>
          <p className="text-charcoal-500 mt-1 text-sm">Resumen en tiempo real del sitio CMIC.</p>
        </div>
        <Link to="/es" target="_blank" rel="noreferrer" data-testid="admin-view-public-site" className="inline-flex items-center gap-2 text-sm font-medium text-cmic-600 hover:text-cmic-700">
          <Eye size={16} />Ver sitio público
        </Link>
      </header>

      {/* KPI cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-kpis">
        <KpiCard
          testid="kpi-interpreters"
          icon={Users}
          color="cmic"
          label="Intérpretes"
          value={interp.total || 0}
          sub={`${interp.visible_public || 0} visibles · ${interp.expired || 0} vencidos`}
        />
        <KpiCard
          testid="kpi-pending"
          icon={ClipboardCheck}
          color="fuchsia"
          label="Solicitudes pendientes"
          value={s.pending_profile_edits || 0}
          sub={s.pending_profile_edits ? 'Revisión requerida' : 'Todo al día'}
          highlight={(s.pending_profile_edits || 0) > 0}
          to="/admin/profile-edits"
        />
        <KpiCard
          testid="kpi-content"
          icon={Newspaper}
          color="amber"
          label="Noticias y eventos"
          value={(s.news?.total || 0) + (s.events?.total || 0)}
          sub={`${s.news?.private || 0} + ${s.events?.private || 0} privados`}
        />
        <KpiCard
          testid="kpi-contact"
          icon={Mail}
          color="rose"
          label="Mensajes nuevos"
          value={s.contact?.unread || 0}
          sub={s.contact?.unread ? 'Sin leer' : 'Bandeja vacía'}
          highlight={(s.contact?.unread || 0) > 0}
          to="/admin/contact"
        />
      </section>

      {/* Visibility bar + Expiring soon alert */}
      <section className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-6" data-testid="admin-visibility-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={18} className="text-cmic-600" />
            <h3 className="text-sm font-semibold text-charcoal-800">Visibilidad del directorio público</h3>
          </div>
          <div className="text-2xl font-bold text-charcoal-800 mb-1">{visibilityPct}%</div>
          <p className="text-xs text-charcoal-500 mb-4">
            {interp.visible_public || 0} de {interp.total || 0} intérpretes aparecen en el directorio público.
          </p>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-gradient-to-r from-cmic-500 to-cmic-600 transition-all"
              style={{ width: `${visibilityPct}%` }}
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <Legend dot="bg-emerald-500" label="Visibles" value={interp.visible_public || 0} />
            <Legend dot="bg-rose-500" label="Vencidos" value={interp.expired || 0} />
            <Legend dot="bg-gray-400" label="Suspendidos" value={interp.suspended || 0} />
            <Legend dot="bg-amber-500" label="Sin fecha" value={interp.unset || 0} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="admin-expiring-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} className="text-amber-600" />
            <h3 className="text-sm font-semibold text-charcoal-800">Por vencer (30 días)</h3>
          </div>
          {(s.expiring_soon || []).length === 0 ? (
            <p className="text-xs text-charcoal-500">No hay vencimientos próximos.</p>
          ) : (
            <ul className="space-y-2">
              {(s.expiring_soon || []).slice(0, 6).map((e) => (
                <li key={e.slug} className="flex items-center justify-between gap-2 text-sm" data-testid={`expiring-${e.slug}`}>
                  <Link to="/admin/dues" className="truncate text-charcoal-700 hover:text-cmic-600">{e.full_name}</Link>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${e.days_left <= 7 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                    {e.days_left}d
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/admin/dues" className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-cmic-600 hover:gap-2 transition-all">
            Ir a Cuotas <ArrowRight size={12} />
          </Link>
        </div>
      </section>

      {/* Quick actions */}
      <section data-testid="admin-quick-actions">
        <h2 className="text-sm font-semibold text-charcoal-700 uppercase tracking-wider mb-3">Acciones rápidas</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickAction to="/admin/news" icon={Plus} label="Crear noticia" testid="qa-create-news" color="amber" />
          <QuickAction to="/admin/events" icon={Plus} label="Crear evento" testid="qa-create-event" color="rose" />
          <QuickAction to="/admin/interpreters" icon={Users} label="Añadir intérprete" testid="qa-add-interpreter" color="cmic" />
          <QuickAction to="/admin/translations" icon={Globe} label="Editar textos" testid="qa-edit-translations" color="sky" />
        </div>
      </section>

      {/* Activity feed */}
      <section className="grid lg:grid-cols-2 gap-6" data-testid="admin-charts">
        <LanguageBarChart data={languages} />
        <DuesPieChart data={duesDist} />
      </section>

      {/* Top interpreters most-requested */}
      <TopInterpretersCard
        data={topInterpreters}
        period={topPeriod}
        onChangePeriod={setTopPeriod}
      />

      {/* Activity feed */}
      <section className="grid lg:grid-cols-2 gap-6">
        <ActivityCard
          title="Solicitudes recientes"
          icon={ClipboardCheck}
          testid="activity-edits"
          to="/admin/profile-edits"
          items={(s.recent?.profile_edits || []).map((r) => ({
            id: r.id,
            label: r.requested_by_name || r.requested_by_username || 'Anónimo',
            sub: `${Object.keys(r.changes || {}).slice(0, 3).join(', ') || '—'}`,
            time: r.created_at,
            badge: r.status,
          }))}
        />
        <ActivityCard
          title="Mensajes de contacto"
          icon={Mail}
          testid="activity-contact"
          to="/admin/contact"
          items={(s.recent?.contact || []).map((c) => ({
            id: c.id,
            label: c.name || 'Sin nombre',
            sub: c.subject || c.email || '',
            time: c.created_at,
            badge: c.read ? 'read' : 'new',
          }))}
        />
        <ActivityCard
          title="Últimas noticias"
          icon={Newspaper}
          testid="activity-news"
          to="/admin/news"
          items={(s.recent?.news || []).map((n) => ({
            id: n.id,
            label: n.title,
            sub: n.category || '',
            time: n.created_at,
            badge: n.is_private ? 'private' : null,
          }))}
        />
        <ActivityCard
          title="Últimos eventos"
          icon={Calendar}
          testid="activity-events"
          to="/admin/events"
          items={(s.recent?.events || []).map((e) => ({
            id: e.id,
            label: e.title,
            sub: e.starts_at ? new Date(e.starts_at).toLocaleString('es-MX') : '',
            time: e.created_at,
            badge: e.is_private ? 'private' : null,
          }))}
        />
      </section>

      {/* Detailed counters */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-mini-counters">
        <MiniCounter to="/admin/periodicals" icon={BookOpen} color="violet" label="Periodicals" />
        <MiniCounter to="/admin/translations" icon={Globe} color="sky" label="Traducciones" />
        <MiniCounter to="/admin/menus" icon={Eye} color="teal" label="Menús" />
        <MiniCounter to={null} icon={MessageSquare} color="cmic" label="Interno" value={`${s.forum?.threads || 0} hilos · ${s.forum?.posts || 0} respuestas`} />
      </section>
    </div>
  );
}

const COLOR_MAP = {
  cmic: { bg: 'bg-cmic-50', text: 'text-cmic-600', accent: 'border-cmic-200' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600', accent: 'border-amber-200' },
  rose: { bg: 'bg-rose-50', text: 'text-rose-600', accent: 'border-rose-200' },
  fuchsia: { bg: 'bg-fuchsia-50', text: 'text-fuchsia-600', accent: 'border-fuchsia-200' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-600', accent: 'border-violet-200' },
  sky: { bg: 'bg-sky-50', text: 'text-sky-600', accent: 'border-sky-200' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-600', accent: 'border-teal-200' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', accent: 'border-emerald-200' },
};

function KpiCard({ testid, icon: Icon, color, label, value, sub, highlight, to }) {
  const c = COLOR_MAP[color] || COLOR_MAP.cmic;
  const inner = (
    <>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 ${c.bg} ${c.text} rounded-lg flex items-center justify-center`}>
          <Icon size={18} />
        </div>
        {highlight && <span className="text-[9px] font-bold bg-rose-500 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider animate-pulse">Atención</span>}
      </div>
      <div className="text-2xl font-bold text-charcoal-800">{value}</div>
      <div className="text-xs text-charcoal-500 mt-0.5">{label}</div>
      {sub && <div className="text-[11px] text-charcoal-400 mt-1">{sub}</div>}
    </>
  );
  const className = `bg-white rounded-2xl border p-5 transition-all ${highlight ? c.accent : 'border-gray-200'} ${to ? 'hover:border-cmic-300 hover:shadow-sm cursor-pointer' : ''}`;
  if (to) return <Link to={to} data-testid={testid} className={className}>{inner}</Link>;
  return <div data-testid={testid} className={className}>{inner}</div>;
}

function Legend({ dot, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
      <span className="text-charcoal-500">{label}</span>
      <span className="font-semibold text-charcoal-700 ml-auto">{value}</span>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, testid, color }) {
  const c = COLOR_MAP[color] || COLOR_MAP.cmic;
  return (
    <Link to={to} data-testid={testid} className="group bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 hover:border-cmic-300 hover:shadow-sm transition-all">
      <div className={`w-9 h-9 ${c.bg} ${c.text} rounded-lg flex items-center justify-center`}>
        <Icon size={16} />
      </div>
      <span className="text-sm font-medium text-charcoal-700 flex-1">{label}</span>
      <ArrowRight size={14} className="text-charcoal-400 group-hover:text-cmic-500 group-hover:translate-x-0.5 transition-all" />
    </Link>
  );
}

function MiniCounter({ to, icon: Icon, color, label, value }) {
  const c = COLOR_MAP[color] || COLOR_MAP.cmic;
  const inner = (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 ${c.bg} ${c.text} rounded-lg flex items-center justify-center`}>
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-charcoal-700">{label}</div>
        {value && <div className="text-[10px] text-charcoal-400 truncate">{value}</div>}
      </div>
      {to && <ArrowRight size={12} className="text-charcoal-400" />}
    </div>
  );
  if (to) return <Link to={to} className="bg-white rounded-xl border border-gray-200 p-3 hover:border-cmic-300 transition-all">{inner}</Link>;
  return <div className="bg-white rounded-xl border border-gray-200 p-3">{inner}</div>;
}

function ActivityCard({ title, icon: Icon, testid, to, items }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-charcoal-600" />
          <h3 className="text-sm font-semibold text-charcoal-800">{title}</h3>
        </div>
        {to && (
          <Link to={to} className="text-xs text-cmic-600 hover:text-cmic-700 font-medium inline-flex items-center gap-1">
            Ver todo <ArrowRight size={11} />
          </Link>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-charcoal-400 py-6 text-center">Sin actividad reciente.</p>
      ) : (
        <ul className="divide-y divide-gray-100 -mx-1">
          {items.slice(0, 5).map((it) => (
            <li key={it.id} className="flex items-start gap-3 py-2.5 px-1">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-charcoal-800 font-medium truncate">{it.label}</div>
                {it.sub && <div className="text-[11px] text-charcoal-500 truncate">{it.sub}</div>}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {it.badge && <BadgePill badge={it.badge} />}
                {it.time && <span className="text-[10px] text-charcoal-400"><Clock size={9} className="inline mr-0.5" />{timeAgo(it.time)}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BadgePill({ badge }) {
  const map = {
    pending: { cls: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Pendiente' },
    approved: { cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, label: 'Aprobada' },
    rejected: { cls: 'bg-rose-100 text-rose-700', icon: XCircle, label: 'Rechazada' },
    new: { cls: 'bg-cmic-100 text-cmic-700', icon: Mail, label: 'Nuevo' },
    read: { cls: 'bg-gray-100 text-gray-600', icon: null, label: 'Leído' },
    private: { cls: 'bg-fuchsia-100 text-fuchsia-700', icon: null, label: 'Privado' },
  };
  const m = map[badge];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${m.cls}`}>
      {Icon && <Icon size={9} />}{m.label}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d`;
    return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

function LanguageBarChart({ data }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="chart-languages">
      <h3 className="text-sm font-semibold text-charcoal-800 mb-1">Distribución de idiomas activos</h3>
      <p className="text-xs text-charcoal-500 mb-4">Cuántos intérpretes ofrecen cada idioma activo (TOP 12).</p>
      {data.length === 0 ? (
        <p className="text-xs text-charcoal-400 py-8 text-center">Sin datos.</p>
      ) : (
        <ul className="space-y-2">
          {data.map((d) => {
            const pct = Math.max(2, Math.round(100 * d.value / max));
            return (
              <li key={d.label} className="flex items-center gap-3 text-xs">
                <span className="w-20 text-charcoal-600 truncate flex-shrink-0">{d.label}</span>
                <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cmic-500 to-cmic-600 rounded-full flex items-center justify-end pr-2 transition-all" style={{ width: `${pct}%` }}>
                    <span className="text-[10px] font-bold text-white">{d.value}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function DuesPieChart({ data }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  // SVG donut chart
  let acc = 0;
  const slices = data.filter((d) => d.value > 0).map((d) => {
    const start = acc / total;
    acc += d.value;
    const end = acc / total;
    return { ...d, start, end };
  });
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="chart-dues">
      <h3 className="text-sm font-semibold text-charcoal-800 mb-1">Estado de cuotas</h3>
      <p className="text-xs text-charcoal-500 mb-4">Distribución de los {total} intérpretes registrados.</p>
      {total === 0 ? (
        <p className="text-xs text-charcoal-400 py-8 text-center">Sin datos.</p>
      ) : (
        <div className="flex items-center gap-6">
          <svg viewBox="-1.1 -1.1 2.2 2.2" className="w-36 h-36 -rotate-90 flex-shrink-0" data-testid="chart-dues-svg">
            {slices.map((s, i) => {
              const x1 = Math.cos(2 * Math.PI * s.start);
              const y1 = Math.sin(2 * Math.PI * s.start);
              const x2 = Math.cos(2 * Math.PI * s.end);
              const y2 = Math.sin(2 * Math.PI * s.end);
              const largeArc = s.end - s.start > 0.5 ? 1 : 0;
              const d = `M ${x1} ${y1} A 1 1 0 ${largeArc} 1 ${x2} ${y2} L 0 0 Z`;
              return <path key={i} d={d} fill={s.color} stroke="white" strokeWidth={0.02} />;
            })}
            <circle r="0.55" fill="white" />
          </svg>
          <ul className="space-y-2 flex-1 min-w-0">
            {data.map((d) => (
              <li key={d.label} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: d.color }} />
                <span className="text-charcoal-600 truncate flex-1">{d.label}</span>
                <span className="font-semibold text-charcoal-800 tabular-nums">{d.value}</span>
                <span className="text-charcoal-400 tabular-nums text-[10px]">{total ? Math.round(100 * d.value / total) : 0}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TopInterpretersCard({ data, period, onChangePeriod }) {
  if (!data) {
    return (
      <section className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="admin-top-interpreters-card">
        <div className="text-xs text-charcoal-400">Cargando solicitudes…</div>
      </section>
    );
  }
  const rows = data.rows || [];
  const total = data.total_requests_in_period || 0;
  const maxCount = Math.max(1, ...rows.map((r) => r.count));
  const periodOptions = [
    { value: 7, label: '7 días' },
    { value: 30, label: '30 días' },
    { value: 90, label: '90 días' },
    { value: 365, label: '12 meses' },
  ];

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="admin-top-interpreters-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp size={18} className="text-cmic-600" />
            <h3 className="text-sm font-semibold text-charcoal-800">Intérpretes más solicitados</h3>
          </div>
          <p className="text-xs text-charcoal-500 mt-1">
            {total === 0
              ? 'Aún no hay solicitudes en este periodo.'
              : `${total} solicitud${total === 1 ? '' : 'es'} a través del directorio en los últimos ${data.period_days} días.`}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5" data-testid="top-interpreters-period-selector">
          {periodOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChangePeriod(opt.value)}
              data-testid={`top-period-${opt.value}`}
              className={`px-3 py-1 text-[11px] font-medium rounded-md transition-colors ${period === opt.value ? 'bg-white text-cmic-700 shadow-sm' : 'text-charcoal-500 hover:text-charcoal-700'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-10 text-charcoal-400" data-testid="top-interpreters-empty">
          <Users size={28} className="mx-auto mb-2 text-charcoal-300" />
          <p className="text-xs">Cuando un cliente envíe el formulario desde el perfil de un intérprete, aparecerá aquí.</p>
        </div>
      ) : (
        <ol className="space-y-2.5">
          {rows.map((r, idx) => {
            const pct = Math.round(100 * (r.count / maxCount));
            const inactive = !r.is_active;
            return (
              <li
                key={r.slug}
                data-testid={`top-interpreter-row-${r.slug}`}
                className="flex items-center gap-3 group"
              >
                <span className={`flex-shrink-0 w-6 text-xs font-bold tabular-nums text-center ${idx < 3 ? 'text-cmic-700' : 'text-charcoal-400'}`}>
                  #{idx + 1}
                </span>
                {r.photo_url ? (
                  <img
                    src={absoluteUrl(r.photo_url)}
                    alt={r.interpreter_name}
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-cmic-50 flex-shrink-0"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cmic-400 to-cmic-600 text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                    {(r.interpreter_name || r.slug).split(' ').slice(0, 2).map((s) => s.charAt(0)).join('')}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Link
                      to={`/admin/interpreters?slug=${encodeURIComponent(r.slug)}`}
                      className="text-sm font-medium text-charcoal-800 truncate hover:text-cmic-600 transition-colors"
                    >
                      {r.interpreter_name}
                    </Link>
                    {inactive && (
                      <span className="text-[9px] uppercase tracking-wider bg-gray-100 text-charcoal-500 px-1.5 py-0.5 rounded">
                        suspendido
                      </span>
                    )}
                    {!r.has_email && (
                      <span
                        className="text-[9px] uppercase tracking-wider bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200"
                        title="No tiene email registrado — las solicitudes se desviaron al CMIC"
                      >
                        sin email
                      </span>
                    )}
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cmic-500 to-cmic-600 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {(r.delivered_to_interpreter > 0 || r.delivered_to_cmic > 0) && (
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-charcoal-400">
                      {r.delivered_to_interpreter > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {r.delivered_to_interpreter} al intérprete
                        </span>
                      )}
                      {r.delivered_to_cmic > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          {r.delivered_to_cmic} al CMIC
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <div className="text-lg font-bold text-charcoal-800 tabular-nums">{r.count}</div>
                  <div className="text-[10px] text-charcoal-400">
                    {r.count === 1 ? 'solicitud' : 'solicitudes'}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

